import * as Sentry from '@sentry/react'
import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { URL_MAIN_PAGE } from '@shared/contants'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

import Image_For_0_In_404 from '/icons/404-cart-0.svg'

function ErrorFallback ({ error, resetError }: {
  error: unknown;
  resetError: () => void;
}) {
  const { t } = useTranslation('common')
  const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка'

  useEffect(() => {
    document.documentElement.dataset.status = '500'
    return () => {
      delete document.documentElement.dataset.status
    }
  }, [])

  return (
    <Container>
      <Wrapper style={{
        padding: '100px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px'
      }}>
        <div role="alert" className={styles.errorContainer}>
          <div className={styles.errorCode}>
            <span>5</span>
            <img src={Image_For_0_In_404} alt="0" />
            <span>0</span>
          </div>

          <h2 className={styles.title}>
            {t('error-boundary.title')}
          </h2>

          <p className={styles.description}>
            {t('error-boundary.description')}
          </p>

          <details className={styles.details}>
            <summary>
              {t('error-boundary.show-details')}
            </summary>
            <pre>
              {errorMessage}
            </pre>
          </details>

          <div className={styles.actions}>

            <Button
              onClick={resetError}
              className={styles.button}
              theme="primary"
              size="large"
            >
              {t('error-boundary.try-again')}
            </Button>

            {/* NOTE: Using asHTMLAnchor because there is no router context available from this component */}
            <Button to={URL_MAIN_PAGE} theme="grey" size="large" asHTMLAnchor>{t('button.main')}</Button>
          </div>
        </div>
      </Wrapper>
    </Container>
  )
}

interface SentryErrorBoundaryProps {
  children: ReactNode;
  beforeCapture?: (scope: Sentry.Scope, error: unknown, errorInfo: string) => void;
}

export function SentryErrorBoundary ({
  children,
  beforeCapture
}: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      beforeCapture={(scope, error, errorInfo) => {
        scope.setTag('errorBoundary', true)
        scope.setContext('errorInfo', {
          componentStack: errorInfo,
        })

        if (beforeCapture) {
          beforeCapture(scope, error, errorInfo)
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
